/**
 * TanStack Query hook for fetching structured report from backend.
 *
 * The /api/report endpoint returns findings already organized by organ sections
 * with merge field metadata for direct rendering in the report editor.
 *
 * Processing chain: raw → structured → phrased → report
 */

import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { getApiBaseUrl } from "../config/api";
import { useAuth } from "../context/AuthContext";
import { requireReadyWording } from "../utils/reportWording";

// ============================================================================
// Types - Match backend response models
// ============================================================================

/**
 * Cortex-only: predicted attribute the user can click to add into the
 * finding's phrase. dim is e.g. "laterality"/"size"/"severity"; value is
 * the specific candidate (e.g. "left"/"moderate"). probability is the model's
 * confidence; opLevel is the strictest passing OP (or null if none).
 *
 * Empty array for drafter / ct-suite — those use LLM-rendered phrases.
 */
export interface CandidateAttribute {
  dim: string;
  value: string;
  probability: number;
  opLevel: string | null;
}

/** Already-gated Drafter attribute; the regulated output has no probability. */
export interface SelectedAttribute {
  dim: string;
  value: string;
}

/** One selectable value of an attribute dim, pre-rendered by the engine. */
export interface PhrasingOption {
  value: string;
  text: string; // display token, spec-applied (e.g. "non-calcified", "small-volume")
  probability: number;
}

/** One attribute dim in the engine-emitted phrase. */
export interface PhrasingDim {
  dim: string;
  selected: string | null; // the engine's gated pick (null = abstained / off)
  options: PhrasingOption[];
  // snake_case to match the engine-emitted JSON verbatim (no transform end-to-end).
  // Dims sharing a group are mutually exclusive (e.g. severity/size).
  mutex_group?: string;
}

/** Engine-emitted structured phrase. The report UI joins selected option texts in
 *  order, sentence-cases the first, and appends `base` — no client-side clinical
 *  logic. Present only when the producing engine emitted it (see backend). */
export interface Phrasing {
  base: string;
  dims: PhrasingDim[];
}

/** Origin of an observation, independent of the report's editable wording.
 * Optional only for historical API responses without retained provenance. */
export interface FindingSource {
  attributeSources?: Array<{ dim: string; value: string; pointer: string }>;
  wording?: { generationIdentity: string } | null;
  sourceRef?: string | null;
  sourcePointer?: string | null;
  sourceSnapshot?: {
    artifact_ref: string;
    digest: string;
    device: string;
    device_version: string;
  } | null;
  sourceStatus?: "bound" | "unavailable";
  imageRefs?: Array<{
    sopInstanceUid: string;
    seriesId: string | null;
    sliceIndex: number | null;
    frameOfReferenceUid: string | null;
  }>;
}

export interface MergeField extends FindingSource {
  id: string;
  text: string;
  condition: string;
  seriesId: string | null;
  sliceIndex: number | null;
  /** FrameOfReferenceUID of the series this finding sits in. Null when
   *  the series lacks an FoR in canonical metadata. Cross-viewport jump
   *  consumers bail on mismatch against the viewport's own FoR. */
  frameOfReferenceUid: string | null;
  candidateAttributes?: CandidateAttribute[];
  attributes?: SelectedAttribute[];
  phrasing?: Phrasing | null;
}

export interface ReportSection {
  findings: MergeField[];
  defaultText: string;
  /** Server-owned display label. Present for new and dynamically derived sections. */
  label?: string | null;
}

export interface ToConsiderItem extends FindingSource {
  id: string;
  text: string;
  condition: string;
  section: string;
  conditions: string[]; // legacy — kept for backward compat
  seriesId: string | null;
  sliceIndex: number | null;
  frameOfReferenceUid: string | null;
  candidateAttributes?: CandidateAttribute[];
  attributes?: SelectedAttribute[];
  phrasing?: Phrasing | null;
}

/** The backend registry's per-study layout decision. The same metadata is
 * returned by both the lightweight layout path and /api/report. */
export interface ReportLayoutMeta {
  /** Single-region compatibility signal; null for mixed/unknown coverage. */
  examRegion: string | null;
  /** Complete registry-compatible coverage, including mixed acquisitions. */
  examRegions: string[];
  /** Base, dynamically composed, or safe generic template identity. */
  examType: string;
  /** True only when positive output is outside every registered exam family. */
  templateMismatch: boolean;
}

export interface ReportLayoutResponse {
  meta: ReportLayoutMeta;
}

export interface StructuredReportResponse {
  sections: Record<string, ReportSection>;
  toConsider: ToConsiderItem[];
  meta: ReportLayoutMeta & {
    totalFindings: number;
    detectedCount: number;
    reviewCount: number;
    llmCacheHit: boolean;
    processedAt: string;
    timingMs: number;
    wording?: WordingState;
  };
}

interface WordingState {
  state: "disabled" | "missing" | "pending" | "ready" | "failed" | "interrupted";
  identity?: string;
  deadline?: number;
}

// ============================================================================
// Query Keys
// ============================================================================

export const reportKeys = {
  all: ["report"] as const,
  study: (studyId: string) => [...reportKeys.all, studyId] as const,
  layout: (studyId: string) => [...reportKeys.study(studyId), "layout"] as const,
};

// ============================================================================
// API Fetch Function
// ============================================================================

export async function fetchReport(
  studyId: string,
  token: string,
  signal?: AbortSignal,
): Promise<StructuredReportResponse> {
  const baseUrl = getApiBaseUrl();
  const encodedStudyId = encodeURIComponent(studyId);

  // Backend's verify_aws_backend_access uses HTTPBearer — it only reads
  // the Authorization header, not cookies. withCredentials alone returns 401
  // against the staging Cognito-validated path.
  const response = await axios.get<StructuredReportResponse>(
    `${baseUrl}/api/report/${encodedStudyId}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      signal,
      // 409 exposes readiness/layout without calling an unavailable draft a
      // successful report. Every fresh-document consumer still gates readiness.
      validateStatus: (status) => status === 200 || status === 409,
    },
  );

  if (
    response.status === 409 &&
    !["missing", "pending", "failed", "interrupted"].includes(
      response.data?.meta?.wording?.state ?? "",
    )
  ) {
    throw new Error("The report returned an invalid wording state.");
  }
  return response.data;
}

/** Create the initial draft only after wording settles. Saved documents bypass
 * preparation; once mounted, the editor owns the text. POST is idempotent across
 * tabs and workers, and a failed/interrupted attempt is never repurchased. */
export async function prepareReport(studyId: string, token: string, signal: AbortSignal) {
  const report = await fetchReport(studyId, token, signal);
  let wording = report.meta.wording;
  if (!wording || !["missing", "pending"].includes(wording.state)) {
    requireReadyWording(wording);
    return report;
  }
  const identity = wording.identity;
  const url = `${getApiBaseUrl()}/api/report/${encodeURIComponent(studyId)}/wording`;
  const config = { headers: { Authorization: `Bearer ${token}` }, signal };
  if (wording.state === "missing") {
    wording = (await axios.post<WordingState>(url, undefined, config)).data;
  }
  // A browser/network failure cannot leave the editor waiting indefinitely.
  const stopAt = Date.now() + 125_000;
  while (wording.state === "pending") {
    if (wording.identity !== identity || Date.now() >= stopAt) {
      throw new Error("Draft preparation changed or timed out. Reload to try again.");
    }
    await new Promise<void>((resolve, reject) => {
      signal.throwIfAborted();
      const cancel = () => {
        clearTimeout(timer);
        reject(signal.reason);
      };
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", cancel);
        resolve();
      }, 500);
      signal.addEventListener("abort", cancel, { once: true });
    });
    wording = (await axios.get<WordingState>(url, config)).data;
  }
  if (wording.state === "missing" || (wording.identity && wording.identity !== identity)) {
    throw new Error("Draft sources changed. Reload to prepare the current case.");
  }
  requireReadyWording(wording);
  const resolved = await fetchReport(studyId, token, signal);
  if (
    ["missing", "pending"].includes(resolved.meta.wording?.state ?? "") ||
    (resolved.meta.wording?.identity && resolved.meta.wording.identity !== identity)
  ) {
    throw new Error("Draft sources changed. Reload to prepare the current case.");
  }
  requireReadyWording(resolved.meta.wording);
  return resolved;
}

export async function fetchReportLayout(
  studyId: string,
  token: string,
): Promise<ReportLayoutResponse> {
  const baseUrl = getApiBaseUrl();
  const encodedStudyId = encodeURIComponent(studyId);
  const response = await axios.get<ReportLayoutResponse>(
    `${baseUrl}/api/report/${encodedStudyId}/layout`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return { meta: response.data.meta };
}

// ============================================================================
// Hook
// ============================================================================

export interface UseReportOptions {
  enabled?: boolean;
  prepare?: boolean;
}

export function useReport(studyId: string | null, options: UseReportOptions = {}) {
  const { enabled = true, prepare = true } = options;
  const { token, userId } = useAuth();

  return useQuery({
    queryKey: [...reportKeys.study(studyId ?? ""), userId, prepare],
    queryFn: ({ signal }) =>
      prepare ? prepareReport(studyId!, token!, signal) : fetchReport(studyId!, token!, signal),
    enabled: enabled && !!studyId && !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes - LLM results are cached on backend
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** Resolve only the report layout, without running clinical-language phrasing.
 * Blank-template and AI-off modes use this so they share the exact same backend
 * selector as AI drafting without paying for or exposing generated findings. */
export function useReportLayout(studyId: string | null, options: UseReportOptions = {}) {
  const { enabled = true } = options;
  const { token } = useAuth();

  return useQuery({
    queryKey: reportKeys.layout(studyId ?? ""),
    queryFn: () => fetchReportLayout(studyId!, token!),
    enabled: enabled && !!studyId && !!token,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
}

// ============================================================================
// Jump payload — colocated with the producer types so a single selector
// projects either MergeField or ToConsiderItem down to the (seriesId,
// sliceIndex, FoR) tuple consumed by the cross-viewport jump path. Adding a
// new variant that should jump means extending only this selector.
// ============================================================================

export interface FindingJumpTarget {
  seriesId: string;
  sliceIndex: number;
  frameOfReferenceUid: string | null;
  worldCentroid?: [number, number, number];
  pixelCentroid?: [number, number];
  segmentIndex?: number;
}

export function findingJumpTarget(
  f: Pick<MergeField | ToConsiderItem, "seriesId" | "sliceIndex" | "frameOfReferenceUid">,
): FindingJumpTarget | null {
  if (!f.seriesId || f.sliceIndex == null) return null;
  return {
    seriesId: f.seriesId,
    sliceIndex: f.sliceIndex,
    // axios does no runtime validation; if the BE response ever omits this
    // field the runtime value is `undefined` while the type says `string |
    // null`. Coerce so downstream consumers (the FoR-bail in VolumeViewport)
    // get the declared shape.
    frameOfReferenceUid: f.frameOfReferenceUid ?? null,
  };
}
